import type { Language } from '../types';

/**
 * Един локализиран низ, прочетен на езика на ЧЕТЕЦА.
 *
 * 🔴 Защо съществува: една и съща верига резерви беше преписана на шест места и
 * вече с ДВЕ различни опашки. Измерено на 17.09.2026:
 *
 * ```
 * shared/utils/signedDocumentSource.ts:132   [lang] → bg → en → code     (подписаният документ)
 * frontend/src/pages/ChildDetailPage.tsx:249 [lang] → bg → code          ← друга опашка
 * .../DeclarationMatrixPage/CellDetailsModal.tsx:99   [lang] → bg → …    ← трета
 * frontend/src/components/DynamicField.tsx:88, :172, :194   [lang] → bg → id/value
 * ```
 *
 * Разминаването не е козметично: спрямо машинен код („GDPR_CONSENT") човешкото
 * заглавие на чужд език е по-четимо, затова `en` стои ПРЕДИ резервата. Копията
 * без `en` падат направо на кода. Тоест един и същ родител вижда различно нещо на
 * различни екрани, а никой тест не пази разликата.
 *
 * ⚠️ Резервата е задължителна, не украса: типът гарантира КЛЮЧА, не стойността —
 * `label.de` съществува и може да е `''` (същият капан, намерен независимо в
 * PDF-а от съседна задача). Затова тук се брои празният низ, не липсващият ключ.
 */
export type LocalizedTextSource = Partial<Record<Language | string, string | undefined>> | null | undefined;

/** Първата непразна стойност от веригата — празният низ НЕ е стойност. */
function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return '';
}

/**
 * Текстът на избрания език: `[език] → bg → en → резерва`.
 *
 * Опашката е НАРОЧНО същата като на `liveDeclarationTitle` (подписаният
 * документ): „човешко заглавие на чужд език е по-четимо от машинен код".
 * ⛔ Не преписвай тази верига на място — това е единственият ѝ дом за екранните
 * четци. Разминаването между копия е дефектът, който функцията премахва.
 *
 * @param source  речник език → текст (`LocalizedString`, `Record<Language,string>`…)
 * @param language езикът на читателя
 * @param fallback последен резерв, когато НИТО един превод не е попълнен
 */
export function localizedText(
  source: LocalizedTextSource,
  language: Language | string | undefined,
  fallback?: string | null,
): string {
  return firstNonEmpty(
    language ? source?.[language] : undefined,
    source?.bg,
    source?.en,
    fallback,
  );
}
